import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { waLink, WHATSAPP_DISPLAY, DEFAULT_WA_MESSAGE, REVEAL } from "@/lib/site";

const PRODUCT_OPTIONS = [
  "Wardrobes & Cupboards",
  "Beds",
  "Bedroom Furniture",
  "TV Units",
  "Tables",
  "Dressing Tables",
  "Sofa Beds",
  "Custom Furniture",
];

const inputCls =
  "w-full rounded-xl border border-[#E7DFD5] bg-white px-4 py-3 text-sm text-walnut placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brass/60 focus:border-brass transition";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", product: "", message: "" });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const text =
      `Hello JK Furniture, I am interested in this product. Please share more details.\n\n` +
      `Name: ${form.name}\nPhone: ${form.phone}\nProduct Interested In: ${form.product}\nMessage: ${form.message}`;
    window.open(waLink(text), "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your enquiry — just press send.");
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-sandsubtle border-t border-[#E7DFD5] py-20 sm:py-28">
      <div className="section-shell">
        <div className="max-w-2xl">
          <motion.p variants={REVEAL} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="eyebrow">
            07 / Contact Us
          </motion.p>
          <motion.h2
            variants={REVEAL} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-walnut"
          >
            Let's Talk About <span className="italic text-teak">Your Space</span>
          </motion.h2>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.div
            variants={REVEAL} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-5 rounded-2xl bg-noir p-8 sm:p-10 text-sand flex flex-col justify-between"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brass">JK Furniture</p>
              <p className="mt-4 flex items-start gap-3 text-sm text-brasslight/80 leading-relaxed">
                <MapPin size={16} className="mt-0.5 shrink-0 text-brass" />
                Mumbai, Navi Mumbai &amp; Thane, Maharashtra, India
              </p>
              <p className="mt-4 flex items-center gap-3 text-sm text-brasslight/80">
                <Clock size={16} className="shrink-0 text-brass" />
                Monday – Sunday, 9:00 AM – 9:00 PM IST
              </p>
              <p className="mt-8 font-serif text-3xl sm:text-4xl tracking-tight">{WHATSAPP_DISPLAY}</p>
              <p className="mt-2 text-sm text-brasslight/70">
                The fastest way to reach us — share room photos, sizes or a product you like.
              </p>
            </div>
            <a
              data-testid="contact-whatsapp-direct-button"
              href={waLink(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 text-sm font-semibold text-noir transition-colors duration-200 hover:bg-[#1EBE5D]"
            >
              <MessageCircle size={17} />
              Chat on WhatsApp
            </a>
          </motion.div>

          <motion.form
            variants={REVEAL} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            data-testid="enquiry-form"
            onSubmit={submit}
            className="lg:col-span-7 rounded-2xl border border-[#E7DFD5] bg-white p-8 sm:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="enquiry-name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Name</label>
                <input
                  id="enquiry-name"
                  data-testid="enquiry-input-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-phone" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Phone Number</label>
                <input
                  id="enquiry-phone"
                  data-testid="enquiry-input-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="e.g. 98200 12345"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="enquiry-product" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Product Interested In</label>
              <select
                id="enquiry-product"
                data-testid="enquiry-select-category"
                required
                value={form.product}
                onChange={set("product")}
                className={inputCls}
              >
                <option value="" disabled>Select a category</option>
                {PRODUCT_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              <label htmlFor="enquiry-message" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone-500">Message</label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-input-message"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us your requirements, room dimensions or timeline…"
                className={inputCls}
              />
            </div>
            <button
              data-testid="enquiry-form-submit-button"
              type="submit"
              className="group mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-walnut px-8 py-4 text-sm font-semibold text-sand transition-colors duration-200 hover:bg-noir"
            >
              Send Enquiry via WhatsApp
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="mt-3 text-xs text-stone-500">
              Submitting opens WhatsApp with your details pre-filled — nothing is stored on our servers.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
